import {calcUserScore, filterObjectByParams} from 'helpers/common-helper';
import BasePlayer from 'services/sports/nhl/player/player';
import Const from 'constants';
import Action from 'actions';
import {getDefaultExposure} from 'helpers/player-helper';

export default class PlayerShowdown extends BasePlayer {
    preparePoolsOfPlayers({incomingValues: {players, filterPlayersBeforePreparingPools}}) {
        if (!filterPlayersBeforePreparingPools) {
            return this.exec('createPoolsOfPlayers', {
                players: Object.values(players),
            });
        }

        const {
            playersData: {filters},
        } = Action.getState();

        const params = {
            team: {
                filterType: Const.FILTER_TYPE_IN_ARRAY,
                filterValue: filters.games,
            },
        };

        players = filterObjectByParams(players, params, true);

        return this.exec('createPoolsOfPlayers', {
            players,
        });
    }

    resetPoints({incomingValues: {dispatch}}) {
        return this.exec('resetPoints_ShowDown', {dispatch});
    }

    resetSettings({incomingValues: {dispatch}}) {
        return this.exec('resetSettings_ShowDown', {dispatch});
    }

    createPoolsOfPlayers({incomingValues: {players}}) {
        return this.exec('createPoolsOfPlayers_ShowDown', {players});
    }

    getExpandedEntityParams({incomingValues = {}}) {
        const {modesConfig} = Action.getState();
        const {userPointsType = 'adminPoints'} = incomingValues;
        const expandedPlayer = super.getExpandedEntityParams({incomingValues});
        const userPoints = parseFloat(expandedPlayer[userPointsType]);
        const cptPointCoef = modesConfig.getConfig('CPT').getPointCoefficient();
        const captainUserPoints = userPoints * cptPointCoef;
        const captainAdminPoints = expandedPlayer.adminPoints * cptPointCoef;
        const captainPoints = expandedPlayer.points * cptPointCoef;
        const captainSalary = expandedPlayer.salary * cptPointCoef;
        const captainFantasyPoints = expandedPlayer.fantasyPoints * cptPointCoef;
        const PlayerService = this;

        return {
            ...expandedPlayer,
            userPoints,
            [Const.EXCLUDED_BY_LINE_GROUPS_RANGE]: false,
            overall_exposureMax: getDefaultExposure('max'),
            lineGroup: parseInt(expandedPlayer.lineGroup),
            userLineGroup: parseInt(expandedPlayer.lineGroup),
            ppLine: parseInt(expandedPlayer.ppLine),
            userPpLine: parseInt(expandedPlayer.ppLine),
            userScore: calcUserScore(userPoints, expandedPlayer.salary, this.userScoreMultiplier),
            lineupRole: Const.DEFAULT_MODE,
            captain_fantasyPoints: parseFloat(captainFantasyPoints.toFixed(3)),
            captain_salary: captainSalary,
            captain_exposureMax: expandedPlayer.exposureMax,
            captain_exposureMin: expandedPlayer.exposureMin,
            captain_points: parseFloat(captainPoints.toFixed(3)),
            captain_adminPoints: parseFloat(captainAdminPoints.toFixed(3)),
            captain_userPoints: parseFloat(captainUserPoints.toFixed(3)),
            captain_userScore: calcUserScore(captainUserPoints, captainSalary, this.userScoreMultiplier),
            captain_pointsChangedManually: false,
            isExcludedByFilter() {
                return this[Const.EXCLUDED_BY_LINE_GROUPS_RANGE];
            },
            include() {
                this.includeBy([Const.EXCLUDED_BY_LINE_GROUPS_RANGE]);
            },
            isGoalie() {
                return this.positionName === 'G';
            },
            isDefenseman() {
                return this.positionName === 'D';
            },
            resetExposure() {
                this.overall_exposureMax = getDefaultExposure('max');
                this.exposureMin = getDefaultExposure('min');
                this.exposureMax = getDefaultExposure('max');
                this.captain_exposureMin = getDefaultExposure('min');
                this.captain_exposureMax = getDefaultExposure('max');
            },
            resetPPLine() {
                this.userPpLine = this.ppLine;
            },
            resetLineGroups() {
                this.userLineGroup = this.lineGroup;
            },
            getPropNameOfMode(prop, incomingMode) {
                const mode = incomingMode || this.mode;

                return PlayerService.exec('definePropNameOfMode', {prop, mode});
            },
            getPropNameOfLineupRole(prop) {
                return this.getIsCaptain() ? this.getCapPropName(prop) : prop;
            },
            getPropValueOfLineupRole(prop) {
                let value,
                    propName = this.getPropNameOfLineupRole(prop);

                if (this.hasOwnProperty(propName)) {
                    value = this[propName];
                } else if (this.hasOwnProperty(prop)) {
                    value = this[prop];
                }

                return value;
            },
            getCapPropName(prop) {
                const mode = this.getIsCaptain() ? Const.CAPTAIN_MODE : Const.DEFAULT_MODE;

                return PlayerService.exec('definePropNameOfMode', {prop, mode});
            },
            getCapPropValue(prop) {
                let value,
                    propName = this.getCapPropName(prop);
                if (this.hasOwnProperty(propName)) {
                    value = this[propName];
                } else if (this.hasOwnProperty(prop)) {
                    value = this[prop];
                }

                return value;
            },
            getIsCaptain() {
                return this.lineupRole === Const.CAPTAIN_MODE;
            },
            setAsCaptain() {
                this.lineupRole = Const.CAPTAIN_MODE;
            },
            getLineupRole() {
                return this.lineupRole;
            },
            isFitForLineup(substitutablePlayer) {
                return true;
            },
        };
    }

    definePropNameOfMode({incomingValues: {prop, mode}}) {
        const captainPropsList = [
            'exposureMax',
            'exposureMin',
            'points',
            'adminPoints',
            'userPoints',
            'userScore',
            'salary',
            'fantasyPoints',
            'pointsChangedManually',
        ];
        const overallPropsList = ['exposureMax'];
        if (Const.CAPTAIN_MODE === mode && inArray(prop, captainPropsList)) {
            return 'captain_' + prop;
        } else if (Const.OVERALL_MODE === mode && inArray(prop, overallPropsList)) {
            return 'overall_' + prop;
        }
        return prop;
    }

    afterGettingPlayers({incomingValues: {dispatch}}) {
        const {playersData} = Action.getState();
        const {lineGroupFilter} = playersData;
        const players = playersData.players.all();

        const teams = {};
        Object.values(players).map((player) => {
            teams[player.team] ??= {implied_run_totals: 0};

            if (!teams[player.team].implied_run_totals) {
                teams[player.team].implied_run_totals = player.implied_run_totals;
            }
        });

        dispatch(Action.do('unsetTeamDetails'));
        dispatch(Action.set('TeamDetails', teams));

        dispatch(Action.set('LineGroups', lineGroupFilter.lineGroups));
    }

    loadExtraPlayerDataFromStore({incomingValues: {dispatch, data}}) {
        if (data.hasOwnProperty('lineGroups')) {
            dispatch(Action.set('LineGroupsFromStorage', data.lineGroups));
        }

        return this.exec('loadExtraPlayerDataFromStore_ShowDown', {dispatch, data});
    }

    getExtraPlayerDataToStore({...rest}) {
        return this.exec('getExtraPlayerDataToStore_ShowDown', {});
    }

    preparePlayerDataForDownloading({incomingValues: {player}}) {
        return this.exec('preparePlayerDataForDownloading_ShowDown', {player});
    }

    updatePlayersAfterRefreshingProjections({incomingValues: {indexedPlayers}}) {
        return this.exec('updatePlayersAfterRefreshingProjections_ShowDown', {indexedPlayers});
    }

    applySympathy({incomingValues: {players, playersSympathy}}) {
        return this.exec('applySympathy_ShowDown', {players, playersSympathy});
    }

    preparePlayerForLineup({incomingValues = {}}) {
        return this.exec('preparePlayerForLineup_ShowDown', incomingValues);
    }

    prepareRandomPoints({incomingValues: {}}) {
        return this.exec('prepareRandomPointsByModes', {});
    }
}
